import './KeyDifferences.css';

export interface KeyDifferencesTheme {
  backgroundGradientStart?: string;
  backgroundGradientEnd?: string;
  glowColor1?: string;
  glowColor2?: string;
  titleColor?: string;
  textColor?: string;
  secondaryTextColor?: string;
  checkmarkBgGradientStart?: string;
  checkmarkBgGradientEnd?: string;
  checkmarkColor?: string;
}

export interface FeatureItem {
  text: string;
  subItems?: string[];
}

export interface KeyDifferencesProps {
  title?: string;
  features: FeatureItem[];
  theme?: KeyDifferencesTheme;
}

const defaultTheme: KeyDifferencesTheme = {
  backgroundGradientStart: 'transparent',
  backgroundGradientEnd: 'transparent',
  glowColor1: 'rgba(var(--color1), 0.3)',
  glowColor2: 'rgba(var(--color2), 0.2)',
  titleColor: '#ffffff',
  textColor: '#ffffff',
  secondaryTextColor: 'rgba(255, 255, 255, 0.8)',
  checkmarkBgGradientStart: 'rgb(var(--color1))',
  checkmarkBgGradientEnd: 'rgb(var(--color2))',
  checkmarkColor: '#ffffff',
};

export default function KeyDifferences({
  title = 'Key Differences',
  features,
  theme = {},
}: KeyDifferencesProps) {
  const appliedTheme = { ...defaultTheme, ...theme };

  return (
    <section id="key-differences" className="key-differences-section">
      <div className="key-differences-glow-container">
        <div
          className="key-differences-glow key-differences-glow-1"
          style={{
            background: `radial-gradient(circle at center, ${appliedTheme.glowColor1} 0%, transparent 70%)`,
          }}
        />
        <div
          className="key-differences-glow key-differences-glow-2"
          style={{
            background: `radial-gradient(circle at center, ${appliedTheme.glowColor2} 0%, transparent 70%)`,
          }}
        />
      </div>

      <div className="key-differences-container">
        <h1
          className="key-differences-title"
          style={{ color: appliedTheme.titleColor }}
          data-testid="text-title"
        >
          {title}
        </h1>

        <div className="key-differences-features">
          {features.map((feature, index) => (
            <div
              key={index}
              className="key-differences-feature-item"
              data-testid={`feature-item-${index}`}
            >
              <div className="key-differences-feature-main">
                <div
                  className="key-differences-checkmark"
                  style={{
                    background: `linear-gradient(135deg, ${appliedTheme.checkmarkBgGradientStart} 0%, ${appliedTheme.checkmarkBgGradientEnd} 100%)`,
                  }}
                  data-testid={`icon-checkmark-${index}`}
                >
                  <svg
                    width="14"
                    height="11"
                    viewBox="0 0 14 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 5.5L5 9.5L13 1.5"
                      stroke={appliedTheme.checkmarkColor}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <p
                  className="key-differences-feature-text"
                  style={{ color: appliedTheme.textColor }}
                  data-testid={`text-feature-${index}`}
                >
                  {feature.text}
                </p>
              </div>

              {feature.subItems && feature.subItems.length > 0 && (
                <div className="key-differences-subitems">
                  {feature.subItems.map((subItem, subIndex) => (
                    <div
                      key={subIndex}
                      className="key-differences-subitem"
                      data-testid={`subitem-${index}-${subIndex}`}
                    >
                      <div
                        className="key-differences-checkmark key-differences-checkmark-small"
                        style={{
                          background: `linear-gradient(135deg, ${appliedTheme.checkmarkBgGradientStart} 0%, ${appliedTheme.checkmarkBgGradientEnd} 100%)`,
                        }}
                      >
                        <svg
                          width="12"
                          height="9"
                          viewBox="0 0 14 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M1 5.5L5 9.5L13 1.5"
                            stroke={appliedTheme.checkmarkColor}
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <p
                        className="key-differences-subitem-text"
                        style={{ color: appliedTheme.secondaryTextColor }}
                        data-testid={`text-subitem-${index}-${subIndex}`}
                      >
                        {subItem}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
